import React, { useState, useEffect, useMemo, useCallback } from "react";
import { flushSync } from "react-dom";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import "../styles/EditarListaDeEmpaqueModal.css";
import TotalesEncabezadoListaEmpaque from "../Componentes/Totalesencabezadolistaempaque";
import LoadingOverlay from "../Componentes/LoadingOverlay";
import { fetchWithAuth, API_URL } from "../Componentes/fetchWithAuth";

//const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const EditarListaDeEmpaqueModal = ({
  isOpen,
  onClose,
  pedido,
  onSaveSuccess,
  language,
}) => {
  const [previewData, setPreviewData] = useState([]); // Datos cargados para previsualización
  const [loading, setLoading] = useState(false);
  const toast = React.useRef(null);
  const isMounted = React.useRef(false);

  // **Encabezados esperados**
  const columnasEsperadas = useMemo(
    () => [
      { field: "nroCaja", header: "NRO CAJA", width: "100px" },
      { field: "RefInterna", header: "REF PRODUCTO", width: "150px" },
      { field: "RefProveedor", header: "REF PROVEEDOR", width: "150px" },
      { field: "ImagenProducto", header: "FOTO", width: "180px" },
      { field: "MarcaCaja", header: "MARCA", width: "120px" },
      { field: "Descripcion", header: "DESCRIPCION", width: "350px" },
      { field: "Material", header: "MATERIAL", width: "120px" },
      { field: "Serial", header: "SERIAL", width: "120px" },
      { field: "MarcaProducto", header: "MARCA (PRODUCTO)", width: "150px" },
      { field: "UnidadMedida", header: "UNIDAD MEDIDA", width: "100px" },
      { field: "Modelo", header: "MODELO", width: "150px" },
      { field: "PesoBruto", header: "PESO BRUTO", width: "110px" },
      { field: "CantCajas", header: "CANT CAJAS", width: "100px" },
      { field: "CantPiezasPorCaja", header: "PCS / CAJA", width: "100px" },
      { field: "CantTotal", header: "CANT TOTAL", width: "100px" },
      { field: "CBMTotal", header: "CBM TOTAL", width: "100px" },
      { field: "CubCaja", header: "CUBICAJE / CAJA", width: "120px" },
      { field: "PesoCaja", header: "PESO / CAJA", width: "110px" },
      { field: "TipoMcia", header: "TIPO MCIA", width: "100px" },
      { field: "Importador", header: "IMPORTADOR", width: "150px" },
    ],
    [],
  );

  const translations = {
    es: {
      titulo: "Editar Lista de Empaque",
      cancelar: "Cancelar",
      guardar: "Guardar",
      advertencia: "Advertencia",
      noDatos: "No hay datos cargados.",
      guardadoExito: "Datos guardados con éxito.",
      guardadoError: "Error al guardar los datos.",
    },
    en: {
      titulo: "Edit Packing List",
      cancelar: "Cancel",
      guardar: "Save",
      advertencia: "Warning",
      noDatos: "No data loaded.",
      guardadoExito: "Data saved successfully.",
      guardadoError: "Error saving data.",
    },
    zh: {
      titulo: "编辑装箱单",
      cancelar: "取消",
      guardar: "保存",
      advertencia: "警告",
      noDatos: "未加载数据。",
      guardadoExito: "数据保存成功。",
      guardadoError: "保存数据时出错。",
    },
  };

  const t = translations[language] || translations.es;

  useEffect(() => {
    isMounted.current = true; // El componente está montado
    if (isOpen) {
      flushSync(() => {
        setLoading(true);
      });
      // Yielding para asegurar que el spinner se pinte antes del fetch
      setTimeout(() => {
        fetchWithAuth(`${API_URL}/api/editar-lista-empaque/${pedido.idpedido}`)
          .then((response) => response.json())
          .then((data) => {
            console.log(
              "Datos recibidos del backend en EditarListaDeEmpaqueModal:",
              data,
            ); // Log para depuración
            if (isMounted.current) {
              // Solo actualiza el estado si el componente sigue montado
              setPreviewData(data);
            }
          })
          .catch((err) => {
            console.error("Error al cargar la lista de empaque:", err);
            if (toast.current && isMounted.current) {
              toast.current.show({
                severity: "error",
                summary: t.guardadoError,
                detail: t.guardadoError,
              });
            }
          })
          .finally(() => {
            if (isMounted.current) setLoading(false);
          });
      }, 50);
    }
    return () => {
      isMounted.current = false; // El componente se desmonta
    };
  }, [isOpen, pedido.idpedido, t.guardadoError]);

  const onRowEditComplete = useCallback(
    (e) => {
      const { newData, index } = e;
      const updatedData = [...previewData];
      updatedData[index] = newData;
      setPreviewData(updatedData);
    },
    [previewData],
  );

  /************** Totales de mercancía y array para mostrarlos **************/

  const handleSave = async () => {
    flushSync(() => {
      setLoading(true);
    });
    // Yielding del hilo principal
    await new Promise((resolve) => setTimeout(resolve, 0));

    if (previewData.length === 0) {
      if (toast.current && isMounted.current) {
        toast.current.show({
          severity: "warn",
          summary: t.advertencia,
          detail: t.noDatos,
        });
      }
      setLoading(false);
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${API_URL}/api/editar-lista-empaque/${pedido.idpedido}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ data: previewData }),
        },
      );

      if (response.ok) {
        if (toast.current && isMounted.current) {
          toast.current.show({
            severity: "success",
            summary: t.guardadoExito,
            detail: "",
          });
        }
        if (onSaveSuccess) onSaveSuccess();
        onClose();
      } else {
        const error = await response.json();
        if (toast.current && isMounted.current) {
          toast.current.show({
            severity: "error",
            summary: t.guardadoError,
            detail: error.message || t.guardadoError,
          });
        }
      }
    } catch (err) {
      console.error("Error al guardar la lista de empaque:", err);
      if (toast.current && isMounted.current) {
        toast.current.show({
          severity: "error",
          summary: t.guardadoError,
          detail: t.guardadoError,
        });
      }
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  return (
    <>
      {loading && <LoadingOverlay message="Procesando datos..." />}
      <Dialog
        header={t.titulo}
        visible={isOpen}
        style={{ width: "85vw", height: "85vh" }}
        breakpoints={{ "1200px": "90vw", "960px": "95vw", "640px": "98vw" }}
        modal
        contentStyle={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
        onHide={onClose}
      >
        <Toast ref={toast} />

        <TotalesEncabezadoListaEmpaque
          pedido={pedido}
          previewData={previewData}
        />

        {/* Tabla memoizada para evitar bloqueos del hilo principal al cambiar el estado loading */}
        {useMemo(
          () =>
            previewData.length > 0 && (
              <DataTable
                value={previewData}
                scrollable
                scrollHeight="flex"
                className="full-height-table"
                style={{ flex: "1 1 auto", minHeight: 0 }}
                tableStyle={{ minWidth: "2200px" }}
                editMode="row"
                onRowEditComplete={onRowEditComplete}
              >
                {columnasEsperadas.map((col) => (
                  <Column
                    key={col.field}
                    field={col.field}
                    header={col.header}
                    style={{
                      minWidth: col.width,
                      textAlign: "center",
                    }}
                    editor={(options) => (
                      <InputText
                        type="text"
                        value={options.value}
                        onChange={(e) => options.editorCallback(e.target.value)}
                      />
                    )}
                  />
                ))}
                <Column
                  rowEditor
                  headerStyle={{ width: "10%", minWidth: "100px" }}
                  bodyStyle={{ textAlign: "center" }}
                />
              </DataTable>
            ),
          [previewData, onRowEditComplete, columnasEsperadas],
        )}

        <div className="p-dialog-footer">
          <Button
            id="btn-editar-le-guardar" // ID descriptivo
            label={t.guardar}
            icon="pi pi-save"
            className="p-button-success"
            onClick={handleSave}
          />
          <Button
            id="btn-editar-le-cancelar" // ID descriptivo
            label={t.cancelar}
            icon="pi pi-times"
            className="p-button-secondary"
            onClick={onClose}
          />
        </div>
      </Dialog>
    </>
  );
};

export default EditarListaDeEmpaqueModal;
